import React from 'react';
import { STAGES, countByStage } from '../lib/ticketStage';
import './TicketStageTabs.css';

/**
 * The stage tabs, with counts, over a list of tickets.
 *
 * WHY THE COUNTS ARE NOT OPTIONAL. The contractor portal had six buttons and
 * no counts, filtering on an exact status match - so a ticket in one of the two
 * statuses no button covered was in the list under All and nowhere else, and
 * nothing on screen could have told you. Gordon's actual request was "if I have
 * 20 tickets and I go through the different classifications, they are all
 * there - so if I missed anything let me know." A count on every tab is how
 * that question gets answered by looking rather than by counting rows.
 *
 * The stages are a PARTITION (lib/ticketStage), so the numbers on the tabs add
 * up to the number on All. When they do not, the row says so out loud instead
 * of quietly losing tickets: `unaccounted` below can only be non-zero if the
 * rule has drifted, and that message is aimed at whoever broke it.
 */
/* `counts` may be given instead of `tickets` - {all, awaiting, ...} - when the
   list is paged on the server and the browser holds one page of it (All
   tickets). The partition check below applies either way. */
const TicketStageTabs = ({ tickets, counts: given, stage, onStage, idPrefix = 'stage' }) => {
  const counts = given && Number.isFinite(given.all) ? given : countByStage(tickets);
  const summed = STAGES.reduce((n, s) => n + counts[s.key], 0);
  const unaccounted = counts.all - summed;

  return (
    <div className="stg">
      <div className="stg-row" role="tablist" aria-label="Ticket stage">
        <button
          type="button"
          id={`${idPrefix}-all`}
          role="tab"
          aria-selected={stage === 'all'}
          className="stg-tab"
          data-on={String(stage === 'all')}
          onClick={() => onStage('all')}
        >
          <span>All</span>
          <span className="stg-count">{counts.all}</span>
        </button>

        {STAGES.map((s) => (
          <button
            type="button"
            key={s.key}
            id={`${idPrefix}-${s.key}`}
            role="tab"
            aria-selected={stage === s.key}
            className="stg-tab"
            data-stage={s.key}
            data-on={String(stage === s.key)}
            onClick={() => onStage(s.key)}
            title={s.hint}
          >
            <span>{s.label}</span>
            {/* Zero is shown, not hidden. An empty stage is a fact worth
                having - "nothing is waiting to be billed" is the answer to a
                question, and a tab that disappears when empty makes the row
                change shape as work moves. */}
            <span className="stg-count">{counts[s.key]}</span>
          </button>
        ))}
      </div>

      {unaccounted !== 0 && (
        // Unreachable unless lib/ticketStage stops being a partition, which is
        // exactly the failure this whole thing exists to prevent. Said on
        // screen rather than logged, because the person who notices a missing
        // ticket is the person using the page.
        <p className="stg-warn" role="alert">
          {Math.abs(unaccounted)} ticket{Math.abs(unaccounted) === 1 ? '' : 's'}
          {unaccounted > 0 ? ' are in no stage' : ' are counted twice'}.
          The stages have stopped adding up to the total &mdash; this is a fault,
          not a filter. Use <strong>All</strong> to see everything.
        </p>
      )}
    </div>
  );
};

export default TicketStageTabs;
